import type { MutableRefObject } from 'react';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { requestAPIHoldingData } from 'src/redux/api/actions';
import { apiCallAsync } from 'src/redux/api/saga';
import { SECTION_DETAIL_DATA_KEY } from 'src/redux/constants';
import type { RootState } from 'src/redux/store';
import { getActionAPISource } from 'src/utils';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  showNotification,
} from 'src/utils';

const CROSS_CUTTING_RESULTS_TITLES = [
  "Gender equality and women's empowerment",
  'Protection and accountability to affected populations',
  'Environmental sustainability',
  'Nutrition integration',
  'Protection and accountability to affected people',
  'Enabling nutrition and healthy diets',
  'Empowering women and girls, and advancing equality',
  'Ensuring protection and accountability to affected people',
  'Integrating environmental sustainability',
  'Embedding humanitarian principles and conflict-sensitivity',
  'Promoting localization',
  'Mainstreaming assurance',
];

function isChildOfCrossCuttingResults(sectionTitle = '') {
  return CROSS_CUTTING_RESULTS_TITLES.includes(sectionTitle);
}

interface RefreshContext {
  dispatch: ReturnType<typeof useDispatch>;
  updatedActiveSectionID: MutableRefObject<any>;
  updatedSectionTitle: MutableRefObject<string | null>;
  isPollingActive: MutableRefObject<boolean | null>;
}

function resolveSectionID(each: any, activeSectionID: any, sectionTitle: any) {
  if (activeSectionID === each.section) {
    return each.section;
  }
  if (
    (sectionTitle.startsWith('Strategic outcome') &&
      each.section_name.startsWith('Strategic outcome')) ||
    (isChildOfCrossCuttingResults(sectionTitle) &&
      isChildOfCrossCuttingResults(each.section_name))
  ) {
    return activeSectionID;
  }
  return null;
}

function handleRefreshSuccess(each: any, ctx: RefreshContext) {
  showNotification(
    `Data refresh successfully completed for ${each.section_name}`,
    'success',
    { autoClose: false },
  );
  setLocalStorageItem('delayed_notification', '-', true);

  const sectionID = resolveSectionID(
    each,
    ctx.updatedActiveSectionID.current,
    ctx.updatedSectionTitle.current,
  );

  if (sectionID) {
    ctx.dispatch(
      requestAPIHoldingData(SECTION_DETAIL_DATA_KEY, null, {
        url_format: { _id_: sectionID },
      }),
    );
  }
}

function handleRefreshFailure(each: any) {
  showNotification(`Failed refresh for ${each.section_name}`, 'error', {
    autoClose: false,
  });
  setLocalStorageItem('delayed_notification', '-', true);
}

function pollRefreshStatus(ctx: RefreshContext) {
  const url = `${getActionAPISource('NOTIFICATIONS_DELAYED_URL')}`;
  if (!(parseInt(getLocalStorageItem('delayed_notification')) > 0)) {
    ctx.isPollingActive.current = false;
    return;
  }

  ctx.isPollingActive.current = true;

  apiCallAsync(url, {}).then(response => {
    if (response.status !== 200) return;

    for (const each of response.data) {
      if (each.status === 'success') {
        handleRefreshSuccess(each, ctx);
      } else if (each.status === 'failed') {
        handleRefreshFailure(each);
      }
    }
  });

  setTimeout(() => pollRefreshStatus(ctx), 5000);
}

export const useRefreshDataSource = () => {
  const sectionDetailData = useSelector(
    (state: RootState) => state.viewData[SECTION_DETAIL_DATA_KEY]?.data,
  );
  const viewState = useSelector((state: RootState) => state.viewState);

  const updatedActiveSectionID = useRef(viewState?.activeSectionID);
  const updatedSectionTitle = useRef<string | null>(null);
  const isPollingActive = useRef<boolean | null>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    if (viewState?.activeSectionID) {
      updatedActiveSectionID.current = viewState.activeSectionID;
    }
  }, [viewState.activeSectionID]);

  useEffect(() => {
    if (sectionDetailData?.title) {
      updatedSectionTitle.current = sectionDetailData.title;
    }
  }, [sectionDetailData?.title]);

  function handleRefreshDataSources() {
    const ctx: RefreshContext = {
      dispatch,
      updatedActiveSectionID,
      updatedSectionTitle,
      isPollingActive,
    };
    const url = `${getActionAPISource('SECTION_REFRESH_DATA_SOURCE')}`.replace(
      '_id_',
      viewState.activeSectionID,
    );
    apiCallAsync(url, {}, 'post')
      .then(response => {
        if (response.status !== 200) return;
        showNotification(response.data.result, 'info', {
          autoClose: false,
          toastId: viewState.activeSectionID,
          className: 'data-refresh-notification',
        });
        if (!response.data.extra) return;
        setLocalStorageItem('delayed_notification', '+', true);
        if (!isPollingActive.current) {
          pollRefreshStatus(ctx);
        }
      })
      .catch(error => {
        if (error.response) {
          showNotification(
            `${error.message}: ${error.response.data.error}`,
            'error',
            { autoClose: false },
          );
        }
      });
  }

  return [handleRefreshDataSources];
};
